import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ChevronDown, Wind, Plane, Users, BarChart2, Phone, Trophy,
  CheckCircle2, ArrowRight, Star, ChevronRight, ChevronLeft,
  Share2, MessageCircle, ExternalLink, HelpCircle, X, Plus, Minus
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════ */
const HERO_IMG = "https://agents-download.skywork.ai/image/rt/9f49bcd72e3db9e25700f0b82ad7a52b.jpg";

const spring = { type: "spring" as const, stiffness: 300, damping: 35 };
const springFast = { type: "spring" as const, stiffness: 420, damping: 30 };
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: spring },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

/* ═══════════════════════════════════════════════════════════════
   SVG DECORATIVE COMPONENTS
═══════════════════════════════════════════════════════════════ */

/* @section: typhoon-eye */
function TyphoonEye({ size = 320, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 320 320" className={className} aria-hidden>
      {/* Outer slow ring */}
      <motion.g
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        style={{ originX: "160px", originY: "160px" }}
      >
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const r = 148;
          const rad = (a * Math.PI) / 180;
          const x1 = 160 + (r - 20) * Math.cos(rad);
          const y1 = 160 + (r - 20) * Math.sin(rad);
          const x2 = 160 + (r + 20) * Math.cos(rad + 0.4);
          const y2 = 160 + (r + 20) * Math.sin(rad + 0.4);
          return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeLinecap="round" />;
        })}
        <circle cx="160" cy="160" r="148" stroke="currentColor" strokeWidth="1" strokeOpacity="0.18" fill="none" strokeDasharray="6 12" />
      </motion.g>
      {/* Mid ring - counter */}
      <motion.g
        animate={{ rotate: -360 }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{ originX: "160px", originY: "160px" }}
      >
        <circle cx="160" cy="160" r="108" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.28" fill="none" strokeDasharray="3 9" />
        {[0, 60, 120, 180, 240, 300].map((a) => {
          const r = 108;
          const rad = (a * Math.PI) / 180;
          const x1 = 160 + (r - 14) * Math.cos(rad);
          const y1 = 160 + (r - 14) * Math.sin(rad);
          const x2 = 160 + (r + 14) * Math.cos(rad + 0.5);
          const y2 = 160 + (r + 14) * Math.sin(rad + 0.5);
          return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.38" strokeLinecap="round" />;
        })}
      </motion.g>
      {/* Inner ring */}
      <motion.g
        animate={{ rotate: 360 }}
        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        style={{ originX: "160px", originY: "160px" }}
      >
        <circle cx="160" cy="160" r="68" stroke="currentColor" strokeWidth="2" strokeOpacity="0.45" fill="none" strokeDasharray="4 6" />
      </motion.g>
      {/* Eye center */}
      <motion.circle
        cx="160" cy="160" r="28"
        stroke="currentColor" strokeWidth="2" strokeOpacity="0.6" fill="none"
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        style={{ originX: "160px", originY: "160px" }}
      />
      <circle cx="160" cy="160" r="12" fill="currentColor" fillOpacity="0.5" />
      <motion.circle
        cx="160" cy="160" r="18"
        stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4" fill="none"
        animate={{ opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </svg>
  );
}

/* @section: taiwan-island */
function TaiwanIsland({ size = 80, className = "" }: { size?: number; className?: string }) {
  // Simplified SVG path for Taiwan island outline
  return (
    <svg width={size} height={size * 1.5} viewBox="0 0 80 130" className={className} aria-hidden fill="none">
      <motion.path
        d="M40,6 C50,6 62,12 68,24 C74,36 74,50 70,64 C66,78 60,92 54,104 C48,116 44,124 40,126 C36,124 32,116 26,104 C20,92 14,78 10,64 C6,50 6,36 12,24 C18,12 30,6 40,6 Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeOpacity="0.6"
        fill="currentColor"
        fillOpacity="0.08"
        strokeLinejoin="round"
        animate={{ pathLength: [0, 1] }}
        transition={{ duration: 2.5, ease: "easeInOut" }}
      />
      {/* Dot for Taipei */}
      <motion.circle cx="40" cy="30" r="3" fill="currentColor" fillOpacity="0.7"
        animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2, repeat: Infinity }}
      />
    </svg>
  );
}

/* @section: basketball-svg */
function BasketballSVG({ size = 40, className = "" }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
      <circle cx="20" cy="20" r="19" stroke="currentColor" strokeWidth="2" />
      <path d="M20 1 C20 1 20 39 20 39" stroke="currentColor" strokeWidth="1.5" />
      <path d="M1 20 C1 20 39 20 39 20" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 8 C12 14 12 26 5 32" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path d="M35 8 C28 14 28 26 35 32" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
  );
}

/* @section: ocean-wave */
function OceanWave({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 1440 80" className={className} preserveAspectRatio="none" aria-hidden>
      <motion.path
        d="M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z"
        fill="currentColor"
        animate={{ d: [
          "M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z",
          "M0,50 C240,10 480,70 720,30 C960,10 1200,70 1440,50 L1440,80 L0,80 Z",
          "M0,40 C240,80 480,0 720,40 C960,80 1200,0 1440,40 L1440,80 L0,80 Z",
        ] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
    </svg>
  );
}

/* @section: speed-lines */
function SpeedLines({ className = "", style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg viewBox="0 0 200 60" className={className} style={style} preserveAspectRatio="none" aria-hidden>
      {[4, 12, 22, 34, 42, 50, 56, 62, 68].map((y, i) => (
        <line key={i} x1="0" y1={y} x2={80 + i * 14} y2={y} stroke="currentColor" strokeWidth="1" strokeOpacity="0.3" />
      ))}
    </svg>
  );
}

/* @section: ct-badge */
function CTBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-black border ${className}`}
      style={{ fontFamily: "Bebas Neue, var(--font-sans)", letterSpacing: "0.08em" }}
    >
      CT · TYPHOON
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   UI PRIMITIVES
═══════════════════════════════════════════════════════════════ */

/* @section: section-title */
function SectionTitle({ icon: Icon, tag, title, sub, accent = false }: {
  icon: React.ElementType; tag: string; title: string; sub?: string; accent?: boolean;
}) {
  return (
    <div className="text-center mb-10">
      <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-4 ${accent ? "border-yellow-400/40 bg-yellow-400/10" : "border-accent/40 bg-accent/10"}`}>
        <Icon size={14} className={accent ? "text-yellow-400" : "text-accent"} />
        <span className={`text-xs font-black tracking-[0.25em] uppercase ${accent ? "text-yellow-400" : "text-accent"}`} style={{ fontFamily: "var(--font-mono)" }}>{tag}</span>
      </div>
      <h2 className="text-3xl md:text-4xl font-black text-white leading-tight mb-3">{title}</h2>
      {sub && <p className="text-white/50 max-w-xl mx-auto leading-relaxed text-sm">{sub}</p>}
    </div>
  );
}

/* @section: choice-card */
function ChoiceCard({ label, desc, selected, onClick }: { label: string; desc?: string; selected: boolean; onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.97 }}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 relative overflow-hidden ${selected
        ? "border-accent bg-accent/12 shadow-[0_0_20px_rgba(234,88,12,0.25),inset_0_0_20px_rgba(234,88,12,0.05)]"
        : "border-white/8 bg-white/3 hover:border-accent/40 hover:bg-white/6"}`}
    >
      {selected && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-3 right-3">
          <CheckCircle2 size={16} className="text-accent" />
        </motion.div>
      )}
      {selected && <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-accent/8 to-transparent pointer-events-none" />}
      <span className="font-bold text-white text-sm block mb-1 relative z-10">{label}</span>
      {desc && <span className="text-white/45 text-xs leading-relaxed relative z-10">{desc}</span>}
    </motion.button>
  );
}

/* @section: multi-choice-group */
function MultiChoiceGroup({ options, values, onChange, max }: {
  options: string[]; values: string[]; onChange: (v: string[]) => void; max?: number;
}) {
  const toggle = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter((x) => x !== opt));
    } else if (!max || values.length < max) {
      onChange([...values, opt]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const sel = values.includes(opt);
        return (
          <motion.button
            key={opt}
            whileTap={{ scale: 0.9 }}
            onClick={() => toggle(opt)}
            className={`px-4 py-2 rounded-full text-xs font-bold border transition-all ${sel
              ? "bg-accent border-accent text-white shadow-[0_0_12px_rgba(234,88,12,0.35)]"
              : "border-white/15 text-white/55 hover:border-accent/40 hover:text-white"}`}
          >{opt}</motion.button>
        );
      })}
    </div>
  );
}

/* @section: rating-5 */
function Rating5({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const opts = ["非常支持", "支持", "普通", "不太支持", "完全不支持"];
  return (
    <div className="space-y-2">
      <p className="text-white/70 text-sm mb-3">{label}</p>
      <div className="grid grid-cols-5 gap-2">
        {opts.map((o) => (
          <motion.button key={o} whileTap={{ scale: 0.88 }} onClick={() => onChange(o)}
            className={`py-2 px-1 rounded-lg text-xs font-bold text-center transition-all border ${value === o ? "bg-accent border-accent text-white shadow-[0_0_10px_rgba(234,88,12,0.35)]" : "border-white/12 text-white/45 hover:border-accent/35 hover:text-white"}`}
          >{o}</motion.button>
        ))}
      </div>
    </div>
  );
}

/* @section: step-progress */
const STEP_LABELS = ["代表隊意象", "亞運包機", "職籃整合", "球迷資料", "驗證送出"];
function StepProgress({ current }: { current: number }) {
  return (
    <div className="sticky top-14 z-40 w-full py-3 px-4" style={{ background: "oklch(0.09 0.03 250)/0.95", backdropFilter: "blur(16px)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div className="max-w-3xl mx-auto flex items-center gap-1">
        {STEP_LABELS.map((label, i) => (
          <div key={i} className="flex items-center flex-1">
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
                i < current ? "bg-accent text-white" : i === current ? "bg-accent text-white shadow-[0_0_12px_rgba(234,88,12,0.5)]" : "bg-white/12 text-white/35"
              }`}>
                {i < current ? <CheckCircle2 size={13} /> : i + 1}
              </div>
              <span className={`text-[10px] font-bold text-center hidden md:block transition-colors ${i <= current ? "text-accent" : "text-white/30"}`}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div className="h-0.5 flex-1 mx-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: "oklch(0.65 0.22 35)" }}
                  initial={{ width: "0%" }}
                  animate={{ width: i < current ? "100%" : "0%" }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* @section: accordion */
function Accordion({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/4 transition-colors"
      >
        <span className="text-white font-bold text-sm">{q}</span>
        <motion.div animate={{ rotate: open ? 45 : 0 }} transition={springFast} className="flex-shrink-0">
          <Plus size={18} className="text-accent" />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 text-white/55 text-sm leading-relaxed border-t border-white/8 pt-4">{a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* @section: nav-buttons */
function NavButtons({ onPrev, onNext, step, total, canNext }: {
  onPrev: () => void; onNext: () => void; step: number; total: number; canNext: boolean;
}) {
  return (
    <div className="flex gap-3 mt-8">
      {step > 0 && (
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={onPrev}
          className="flex-1 py-3 rounded-xl border border-white/15 text-white font-bold text-sm flex items-center justify-center gap-2 hover:border-white/30 transition-colors">
          <ChevronLeft size={16} /> 上一步
        </motion.button>
      )}
      <motion.button
        whileHover={{ scale: canNext ? 1.02 : 1 }} whileTap={{ scale: 0.97 }}
        onClick={onNext} disabled={!canNext}
        className={`flex-1 py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all ${canNext
          ? "bg-accent text-white shadow-[0_2px_20px_rgba(234,88,12,0.4)] hover:shadow-[0_2px_28px_rgba(234,88,12,0.6)]"
          : "bg-white/8 text-white/30 cursor-not-allowed"}`}
      >
        {step === total - 1 ? "送出問卷 🏀" : <>下一議題 <ChevronRight size={16} /></>}
      </motion.button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SURVEY STEP COMPONENTS
═══════════════════════════════════════════════════════════════ */

/* @section: step-0-brand */
function StepBrand({ data, setData }: { data: Record<string, unknown>; setData: (d: Record<string, unknown>) => void }) {
  const s = (key: string, val: unknown) => setData({ ...data, [key]: val });
  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-10">
      {/* Visual intro card */}
      <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, oklch(0.15 0.06 220), oklch(0.10 0.04 250))" }}>
        <div className="absolute top-0 right-0 opacity-20">
          <TyphoonEye size={200} className="text-cyan-400" />
        </div>
        <div className="relative z-10 p-6">
          <div className="flex items-start gap-4">
            <TaiwanIsland size={50} className="text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <CTBadge className="border-cyan-400/40 text-cyan-300 bg-cyan-400/10 mb-3" />
              <h3 className="text-white font-black text-xl mb-2">中華颱風隊：一個可以長期累積的代表隊意象</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                國家隊需要的不只是一套球衣，也需要一個能被記住、被傳唱、被年輕球員繼承的共同意象。
                颱風來自海洋，帶著速度、旋轉、壓迫、防守強度與島嶼能量；「Tai 風」也象徵屬於台灣的球風。
              </p>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["颱風眼", "海洋能量", "速度旋轉", "壓迫防守", "島嶼精神"].map((t) => (
              <span key={t} className="px-2 py-0.5 rounded text-xs bg-cyan-400/15 text-cyan-300 border border-cyan-400/20 font-bold">{t}</span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Q1 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q1. 你是否支持台灣籃球代表隊發展一個長期使用的隊伍意象？</p>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {["非常支持", "支持", "普通", "不太支持", "完全不支持"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q1 === o} onClick={() => s("q1", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q2 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q2. 你對「中華颱風隊／Tai 風／CT Typhoon」這個概念的直覺感受是？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["很有台灣感", "很有力量", "適合籃球", "適合國際行銷", "有記憶點但需要修正", "不太適合", "其他"]}
          values={(data.q2 as string[]) ?? []}
          onChange={(v) => s("q2", v)}
        />
      </motion.div>

      {/* Q3 CI */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q3. 請選出你最喜歡的 CI 設計方向</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "A 款：速度旋風", desc: "以風速線條與旋轉軌跡為主視覺，強調快攻與爆發力" },
            { label: "B 款：島嶼風眼", desc: "以颱風眼俯視意象結合台灣地形輪廓，強調島嶼認同" },
            { label: "C 款：壓迫防守", desc: "以密集防守陣形線條為設計語言，強調籃球競技感" },
            { label: "D 款：都不夠好，希望重新設計", desc: "以上三款均無法代表台灣籃球精神" },
          ].map((o) => (
            <ChoiceCard key={o.label} label={o.label} desc={o.desc} selected={data.q3 === o.label} onClick={() => s("q3", o.label)} />
          ))}
        </div>
      </motion.div>

      {/* Q4 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q4. 你認為代表隊視覺應優先傳達什麼？<span className="text-accent text-xs font-normal ml-2">可複選，最多3項</span></p>
        <MultiChoiceGroup
          options={["速度", "團結", "防守壓迫", "台灣島嶼感", "年輕世代感", "國際賽專業感", "街頭與潮流感", "傳統中華隊精神", "其他"]}
          values={(data.q4 as string[]) ?? []}
          onChange={(v) => s("q4", v)}
          max={3}
        />
      </motion.div>

      {/* Q5 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q5. 你希望「中華颱風隊」如何呈現？</p>
        <div className="space-y-2">
          {[
            "CT 為正式名，Typhoon 作為隊伍暱稱與應援品牌",
            "Chinese Taipei Typhoon 並列呈現",
            "中文使用中華颱風隊，英文保留 Chinese Taipei",
            "先不要使用颱風隊，維持現有稱呼",
            "其他",
          ].map((o) => <ChoiceCard key={o} label={o} selected={data.q5 === o} onClick={() => s("q5", o)} />)}
        </div>
      </motion.div>

      {/* Q6 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q6. 你是否願意購買第一批「中華颱風隊」應援商品？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["T-shirt／球衣", "毛巾／旗幟", "徽章／貼紙／吊飾", "限量套組", "看設計與價格再決定", "不會"]}
          values={(data.q6 as string[]) ?? []}
          onChange={(v) => s("q6", v)}
        />
      </motion.div>
    </motion.div>
  );
}

/* @section: step-1-charter */
function StepCharter({ data, setData }: { data: Record<string, unknown>; setData: (d: Record<string, unknown>) => void }) {
  const s = (key: string, val: unknown) => setData({ ...data, [key]: val });
  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-10">
      {/* Visual intro */}
      <motion.div variants={fadeUp} className="rounded-2xl overflow-hidden relative" style={{ background: "linear-gradient(135deg, oklch(0.12 0.05 250), oklch(0.08 0.03 250))" }}>
        <div className="absolute bottom-0 right-0 text-yellow-400 opacity-15 pointer-events-none">
          <Plane size={100} className="transform -rotate-12" />
        </div>
        <div className="relative z-10 p-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-black border border-yellow-400/40 text-yellow-400 bg-yellow-400/10" style={{ fontFamily: "var(--font-mono)" }}>2026 名古屋亞運</span>
            <span className="text-white/40 text-xs">9 月出征</span>
          </div>
          <h3 className="text-white font-black text-xl mb-2">跟著代表隊出發，把應援帶到亞運現場</h3>
          <p className="text-white/60 text-sm leading-relaxed mb-4">籃協正在前測「亞運應援包機」方案，讓球迷用比一般自由行機票更高一些、但一次取得多項應援內容的方式，實際到現場支持代表隊。</p>
          <div className="grid grid-cols-2 gap-2">
            {["去程包機機位", "A 席賽事票券", "首發限量應援包", "機上應援體驗"].map((item) => (
              <div key={item} className="flex items-center gap-2 text-xs text-white/65">
                <CheckCircle2 size={12} className="text-yellow-400" />{item}
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-white/30 leading-relaxed">⚠ 本方案目前為意願調查與合作洽詢階段，一切以後續正式公告為準。</p>
        </div>
      </motion.div>

      {/* Q7 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q7. 若中華籃協推出「亞運應援包機」，你是否有興趣參加？</p>
        <div className="space-y-2">
          {["非常有興趣，價格合理就會報名", "有興趣，需要看日期與價格", "可能會，但需要同行者", "只會看直播，不會出國", "沒興趣"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q7 === o} onClick={() => s("q7", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q8 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q8. 你可接受的方案價格上限是？（含機票＋票券＋應援物）</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {["18,000 元以下", "18,001–22,000 元", "22,001–25,000 元", "25,001–30,000 元", "30,000 元以上也可接受，若內容足夠完整"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q8 === o} onClick={() => s("q8", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q9 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q9. 哪些內容最能提高你的購買意願？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["確定包含中華隊比賽門票", "限量應援 T-shirt", "限量毛巾／旗幟", "機上球員影片", "與代表隊同日出發的儀式感", "支持女籃與三對三升艙的公益感", "有知名球評同行", "有機場集合拍照活動", "價格越低越好"]}
          values={(data.q9 as string[]) ?? []}
          onChange={(v) => s("q9", v)}
        />
      </motion.div>

      {/* Q10 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q10. 你最在意的風險是？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["價格太高", "請假困難", "不確定賽程", "不確定票券位置", "不確定是否真的幫助到球員", "交通與住宿要自己處理太麻煩", "其他"]}
          values={(data.q10 as string[]) ?? []}
          onChange={(v) => s("q10", v)}
        />
      </motion.div>

      {/* Q11 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q11. 若需先支付可退還或可轉讓的意向金，你可接受多少？</p>
        <div className="grid grid-cols-3 gap-2">
          {["不接受意向金", "1,000 元", "2,000 元", "3,000 元", "5,000 元以上"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q11 === o} onClick={() => s("q11", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q12 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q12. 你可能幾人同行？</p>
        <div className="grid grid-cols-3 gap-2">
          {["1 人", "2 人", "3–4 人", "5 人以上", "公司／社團／球隊團體報名"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q12 === o} onClick={() => s("q12", o)} />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* @section: step-2-league */
function StepLeague({ data, setData }: { data: Record<string, unknown>; setData: (d: Record<string, unknown>) => void }) {
  const s = (key: string, val: unknown) => setData({ ...data, [key]: val });
  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-10">
      <motion.div variants={fadeUp} className="p-6 rounded-2xl border border-white/8 bg-white/3">
        <h3 className="text-white font-black text-xl mb-3">台灣職籃，該怎麼整合？</h3>
        <p className="text-white/60 text-sm leading-relaxed mb-4">台灣籃球長期面臨資源分散、聯盟並立、制度不一與球迷市場分裂等問題。新籃協的立場是：台灣籃球需要更清楚的頂級聯賽制度，也需要讓 SBL、職籃、UBA、HBL 與基層養成形成更健康的階層關係。但要走到哪一步，球迷的意見非常重要。</p>
        <div className="flex flex-wrap gap-2">
          {["聯賽認證分級", "外籍球員登錄", "台灣大賽", "SBL 轉型", "裁判制度整合"].map((t) => (
            <span key={t} className="px-2 py-0.5 rounded text-xs bg-white/8 text-white/55 border border-white/12">{t}</span>
          ))}
        </div>
      </motion.div>

      {/* Q13 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q13. 你希望籃協在職籃整合上採取哪一種態度？</p>
        <div className="space-y-3">
          {[
            { label: "A｜積極要求並促成合併", desc: "籃協應使用最積極的制度工具，促成單一頂級職籃；SBL 轉型為下層發展聯盟，類似 NBA 與 G League 的上下層關係。" },
            { label: "B｜積極協調，以軟性促成為主", desc: "籃協應協助兩聯盟合作與合併，但不急著使用強硬手段；台灣大賽可視兩聯盟意願逐步推進。" },
            { label: "C｜先促成台灣大賽即可", desc: "籃協應優先主辦或促成跨聯盟台灣大賽，至於兩聯盟是否正式合併，可留給市場與球團決定。" },
          ].map((o) => <ChoiceCard key={o.label} label={o.label} desc={o.desc} selected={data.q13 === o.label} onClick={() => s("q13", o.label)} />)}
        </div>
      </motion.div>

      {/* Q14 */}
      <motion.div variants={fadeUp}>
        <Rating5 label="Q14. 你支持「台灣只保留一個最高層級職業籃球聯盟」嗎？" value={(data.q14 as string) ?? ""} onChange={(v) => s("q14", v)} />
      </motion.div>

      {/* Q15 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q15. 你是否支持 SBL 轉型為發展聯盟？</p>
        <div className="space-y-2">
          {["支持，應成為職籃二軍／養成聯盟", "支持，但應保留獨立品牌", "不支持，SBL 應維持現狀", "不熟悉 SBL，無法判斷"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q15 === o} onClick={() => s("q15", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q16 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q16. 你是否支持籃協主辦或促成「台灣大賽」？</p>
        <div className="space-y-2">
          {["支持，無論合併與否都應舉辦", "支持，但需兩聯盟共同同意", "不支持，應先完成合併", "不支持，會影響原本聯盟賽制"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q16 === o} onClick={() => s("q16", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q17 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q17. 若兩聯盟持續無法整合，你是否支持籃協使用更強的制度工具推進？</p>
        <div className="space-y-2">
          {["支持，應設定期限", "支持，但要先公開完整規章", "不支持，應尊重聯盟自主", "不確定，需要更多資訊"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q17 === o} onClick={() => s("q17", o)} />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* @section: step-3-fandata */
function StepFanData({ data, setData }: { data: Record<string, unknown>; setData: (d: Record<string, unknown>) => void }) {
  const s = (key: string, val: unknown) => setData({ ...data, [key]: val });
  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-10">
      <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-accent/25 bg-accent/6 text-center">
        <p className="text-white/75 text-sm leading-relaxed">
          「你怎麼看球，會決定台灣籃球怎麼長大。」<br />
          <span className="text-accent font-bold">完成全部填答並通過手機驗證者，可參與中華颱風隊首批相關贈品抽選 🎁</span>
        </p>
      </motion.div>

      {/* Q18 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q18. 你平常會看哪些台灣籃球賽事？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["TPBL", "PLG", "SBL", "WSBL", "UBA", "HBL", "國家隊比賽", "3x3 籃球", "幾乎不看"]}
          values={(data.q18 as string[]) ?? []}
          onChange={(v) => s("q18", v)}
        />
      </motion.div>

      {/* Q19 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q19. 你平均多久看一次台灣籃球？</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {["每週多場", "每週一場", "每月數場", "只看季後賽／冠軍賽", "只看國家隊", "很少看"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q19 === o} onClick={() => s("q19", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q20 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q20. 你一年大約進場看幾場籃球？</p>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {["0 場", "1–2 場", "3–5 場", "6–10 場", "11 場以上"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q20 === o} onClick={() => s("q20", o)} />
          ))}
        </div>
      </motion.div>

      {/* Q21 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q21. 你是否購買過台灣籃球相關商品？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["球衣", "T-shirt", "毛巾", "球員周邊", "聯盟／球隊會員", "從未購買"]}
          values={(data.q21 as string[]) ?? []}
          onChange={(v) => s("q21", v)}
        />
      </motion.div>

      {/* Q22 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q22. 你會看哪些國際籃球內容？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["NBA", "B.LEAGUE", "KBL", "CBA", "EuroLeague", "NCAA", "FIBA 國際賽", "奧運／亞運籃球", "3x3 國際賽", "幾乎不看"]}
          values={(data.q22 as string[]) ?? []}
          onChange={(v) => s("q22", v)}
        />
      </motion.div>

      {/* Q23 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q23. 你最常在哪裡看籃球？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["電視", "YouTube", "付費 OTT", "社群短影音", "現場進場", "運動酒吧／餐廳", "朋友轉傳精華"]}
          values={(data.q23 as string[]) ?? []}
          onChange={(v) => s("q23", v)}
        />
      </motion.div>

      {/* Q24 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-4">Q24. 你願意為哪些籃球內容付費？<span className="text-accent text-xs font-normal ml-2">可複選</span></p>
        <MultiChoiceGroup
          options={["現場門票", "直播訂閱", "國家隊商品", "球員見面會", "國際賽旅遊團", "兒童籃球活動", "不太願意付費"]}
          values={(data.q24 as string[]) ?? []}
          onChange={(v) => s("q24", v)}
        />
      </motion.div>
    </motion.div>
  );
}

/* @section: step-4-verify */
function StepVerify({ data, setData, onSubmit }: { data: Record<string, unknown>; setData: (d: Record<string, unknown>) => void; onSubmit: () => void }) {
  const s = (key: string, val: unknown) => setData({ ...data, [key]: val });
  const codeSent = Boolean(data.codeSent);
  const phoneVerified = Boolean(data.phoneVerified);
  const privacyAgree = Boolean(data.privacyAgree);
  const lotteryAgree = Boolean(data.lotteryAgree);
  const phone = (data.phone as string) ?? "";
  const verifyCode = (data.verifyCode as string) ?? "";
  const canSubmit = phoneVerified && privacyAgree && lotteryAgree;

  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-7">
      {/* Basic info */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black text-base mb-5">基本資料</p>
        <div className="grid md:grid-cols-3 gap-6">
          {/* Age Q25 */}
          <div>
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "var(--font-mono)" }}>Q25 年齡</p>
            <div className="space-y-1.5">
              {["18 歲以下", "18–24 歲", "25–34 歲", "35–44 歲", "45–54 歲", "55 歲以上"].map((a) => (
                <ChoiceCard key={a} label={a} selected={data.q25 === a} onClick={() => s("q25", a)} />
              ))}
            </div>
          </div>
          {/* Gender Q27 */}
          <div>
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "var(--font-mono)" }}>Q27 性別</p>
            <div className="space-y-1.5">
              {["男", "女", "非二元／其他", "不願透露"].map((g) => (
                <ChoiceCard key={g} label={g} selected={data.q27 === g} onClick={() => s("q27", g)} />
              ))}
            </div>
          </div>
          {/* Occupation Q28 */}
          <div>
            <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "var(--font-mono)" }}>Q28 職業</p>
            <div className="space-y-1.5">
              {["學生", "上班族", "自營／自由工作者", "企業主／管理職", "教育／研究", "運動相關產業", "媒體／內容工作者", "其他"].map((o) => (
                <ChoiceCard key={o} label={o} selected={data.q28 === o} onClick={() => s("q28", o)} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* City Q26 */}
      <motion.div variants={fadeUp}>
        <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "var(--font-mono)" }}>Q26 居住縣市</p>
        <input
          type="text"
          value={(data.q26 as string) ?? ""}
          onChange={(e) => s("q26", e.target.value)}
          placeholder="例：台北市、高雄市、台中市…"
          className="w-full px-4 py-3 rounded-xl border text-white placeholder-white/25 focus:outline-none focus:border-accent/60 transition-colors"
          style={{ background: "oklch(0.12 0.03 250)", borderColor: "rgba(255,255,255,0.12)", fontFamily: "var(--font-sans)" }}
        />
      </motion.div>

      {/* Basketball habit Q31 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-3">Q31. 是否有打籃球習慣？</p>
        <div className="grid grid-cols-3 gap-2">
          {["每週都打", "每月會打", "偶爾打", "以前打，現在很少", "幾乎不打"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q31 === o} onClick={() => s("q31", o)} />
          ))}
        </div>
      </motion.div>

      {/* Entertainment budget Q29 */}
      <motion.div variants={fadeUp}>
        <p className="text-white font-black mb-3">Q29. 月可支配娛樂預算</p>
        <div className="grid grid-cols-3 gap-2">
          {["1,000 元以下", "1,001–3,000 元", "3,001–5,000 元", "5,001–10,000 元", "10,001 元以上"].map((o) => (
            <ChoiceCard key={o} label={o} selected={data.q29 === o} onClick={() => s("q29", o)} />
          ))}
        </div>
      </motion.div>

      {/* Notify Q32 */}
      <motion.div variants={fadeUp}>
        <div className="flex items-start gap-3 p-5 rounded-2xl bg-accent/6 border border-accent/20">
          <motion.button whileTap={{ scale: 0.85 }} onClick={() => s("q32", !data.q32)}
            className={`w-6 h-6 rounded-md flex-shrink-0 mt-0.5 flex items-center justify-center border-2 transition-all ${data.q32 ? "bg-accent border-accent" : "border-white/30"}`}>
            {data.q32 && <CheckCircle2 size={13} className="text-white" />}
          </motion.button>
          <div>
            <p className="text-accent font-black text-sm mb-0.5">🎁 Q32. 我願意收到中華籃協後續活動通知</p>
            <p className="text-white/50 text-xs leading-relaxed">代表隊活動、應援包機、商品預購與球迷活動。完成手機驗證者可參與中華颱風隊首批贈品抽選。</p>
          </div>
        </div>
      </motion.div>

      {/* Phone verification */}
      <motion.div variants={fadeUp} className="p-6 rounded-2xl border border-white/10" style={{ background: "oklch(0.12 0.03 250)" }}>
        <p className="text-white font-black text-base mb-4 flex items-center gap-2">
          <Phone size={18} className="text-accent" />
          Q33. 手機驗證（一人一票）
        </p>
        <div className="flex gap-3 mb-3">
          <input
            type="tel"
            value={phone}
            onChange={(e) => s("phone", e.target.value)}
            placeholder="09xxxxxxxx"
            className="flex-1 px-4 py-3 rounded-xl border text-white placeholder-white/25 focus:outline-none focus:border-accent/60 text-base"
            style={{ background: "oklch(0.15 0.03 250)", borderColor: "rgba(255,255,255,0.1)", fontFamily: "var(--font-mono)" }}
          />
          <motion.button
            whileHover={{ scale: !codeSent ? 1.03 : 1 }} whileTap={{ scale: 0.97 }}
            onClick={() => { if (phone.length >= 9) s("codeSent", true); }}
            disabled={codeSent}
            className={`px-5 py-3 rounded-xl font-black text-sm whitespace-nowrap transition-all ${codeSent ? "text-white/35 cursor-not-allowed" : "bg-accent text-white hover:bg-accent/80 shadow-[0_0_14px_rgba(234,88,12,0.35)]"}`}
            style={codeSent ? { background: "oklch(0.15 0.02 250)" } : {}}
          >{codeSent ? "已發送" : "發送驗證碼"}</motion.button>
        </div>

        {codeSent && !phoneVerified && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => s("verifyCode", e.target.value)}
              placeholder="輸入驗證碼"
              className="flex-1 px-4 py-3 rounded-xl border text-white placeholder-white/25 focus:outline-none focus:border-accent/60 text-xl tracking-[0.4em]"
              style={{ background: "oklch(0.15 0.03 250)", borderColor: "rgba(255,255,255,0.1)", fontFamily: "var(--font-mono)" }}
            />
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={() => { if (verifyCode.length >= 4) s("phoneVerified", true); }}
              className="px-5 py-3 rounded-xl bg-accent text-white font-black text-sm hover:bg-accent/80">確認</motion.button>
          </motion.div>
        )}

        {phoneVerified && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 flex items-center gap-2 text-sm font-bold" style={{ color: "oklch(0.72 0.18 145)" }}>
            <CheckCircle2 size={15} /> 手機號碼驗證成功！
          </motion.div>
        )}
      </motion.div>

      {/* Privacy */}
      <motion.div variants={fadeUp} className="p-5 rounded-2xl border border-white/8 bg-white/3 space-y-4">
        <h4 className="text-white font-black">個人資料告知說明</h4>
        <p className="text-white/50 text-xs leading-relaxed">本前測將蒐集手機號碼作為一人一票驗證、抽獎聯繫與防止重複填答之用。手機號碼不會公開呈現，也不會出售予第三方。調查分析將以統計方式進行，不會在對外報告中揭露可識別個人的資料。</p>
        <div className="space-y-3">
          {[
            { key: "privacyAgree", label: "我已閱讀並同意本前測之個資蒐集、處理與利用說明。" },
            { key: "lotteryAgree", label: "我同意以手機號碼作為一人一票與抽獎聯繫使用。" },
          ].map((item) => (
            <div key={item.key} className="flex items-start gap-3">
              <motion.button whileTap={{ scale: 0.85 }} onClick={() => s(item.key, !data[item.key])}
                className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center transition-all ${data[item.key] ? "bg-accent border-accent" : "border-white/30"}`}>
                {data[item.key] && <CheckCircle2 size={11} className="text-white" />}
              </motion.button>
              <span className="text-white/55 text-xs leading-relaxed">{item.label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Submit */}
      <motion.button
        variants={fadeUp}
        whileHover={{ scale: canSubmit ? 1.02 : 1 }}
        whileTap={{ scale: 0.98 }}
        onClick={onSubmit}
        disabled={!canSubmit}
        className={`w-full py-5 rounded-2xl font-black text-lg tracking-widest uppercase transition-all ${canSubmit
          ? "bg-accent text-white shadow-[0_4px_32px_rgba(234,88,12,0.55)] hover:shadow-[0_6px_40px_rgba(234,88,12,0.7)]"
          : "text-white/25 cursor-not-allowed"}`}
        style={!canSubmit ? { background: "oklch(0.15 0.02 250)" } : {}}
      >
        {!canSubmit && !phoneVerified ? "請先完成手機驗證 📱" : !canSubmit ? "請勾選個資同意欄 ✓" : "送出問卷，參與抽獎 🏀"}
      </motion.button>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */

/* @section: main */
export default function Index() {
  const heroRef = useRef<HTMLDivElement>(null);
  const surveyRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = landing, 0-4 = steps
  const [surveyData, setSurveyData] = useState<Record<string, unknown>>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"], // 修正：頁面頂端時 scrollYProgress = 0，文字 opacity = 1
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]); // 60% 後才淡出，不突兀

  const scrollToSurvey = () => {
    setCurrentStep(0);
    setTimeout(() => surveyRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  useEffect(() => {
    if (currentStep >= 0 && surveyRef.current) {
      surveyRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
    else handleSubmit();
  };
  const handlePrev = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };
  const handleSubmit = () => {
    if (!surveyData.phoneVerified || !surveyData.privacyAgree || !surveyData.lotteryAgree) return;
    setSubmitted(true);
    setCurrentStep(-1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const faqs = [
    { q: "這是正式定案嗎？", a: "不是。這是前測網站，用來蒐集球迷對代表隊意象、亞運應援、職籃整合與球迷活動的意見。" },
    { q: "中華颱風隊會取代 Chinese Taipei 嗎？", a: "不會。國際賽事正式名稱仍需符合賽會規範；中華颱風隊是代表隊應援意象與品牌系統的前測概念。" },
    { q: "亞運應援包機一定會成行嗎？", a: "目前仍為意願調查與合作洽詢階段。實際價格、日期、票券、應援物與報名方式，以後續正式公告為準。" },
    { q: "球迷一定會跟球員搭同一班飛機嗎？", a: "不保證。籃協會爭取讓球迷與代表隊在同一段旅程中共同出發，但實際搭乘安排需依代表隊行程、航空安全、座位配置與管理規範確認。" },
    { q: "為什麼要問職籃整合？", a: "因為職籃整合會影響球迷觀賽體驗、球員發展、聯盟制度、代表隊養成與台灣籃球的長期競爭力。籃協希望在推動制度規劃前，先了解球迷立場。" },
    { q: "我的手機號碼會怎麼使用？", a: "手機號碼主要用於一人一票、防止重複填答、抽獎聯繫。若你同意，也可用於後續活動通知。不會出售予第三方。" },
    { q: "會公布調查結果嗎？", a: "籃協將視前測結果整理狀況，規劃公布階段性摘要，讓球迷知道意見如何被看見與討論。" },
  ];

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ background: "oklch(0.09 0.03 250)", fontFamily: "var(--font-sans)" }}>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/8" style={{ background: "oklch(0.09 0.03 250)/0.92", backdropFilter: "blur(16px)" }}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BasketballSVG size={20} className="text-accent" />
            <span className="font-black text-white" style={{ fontFamily: "Bebas Neue, var(--font-sans)", fontSize: "1.2rem", letterSpacing: "0.06em" }}>TAI 風起</span>
            <CTBadge className="hidden md:inline-flex border-accent/30 text-accent/80 bg-accent/8 text-[10px] ml-1" />
          </div>
          <div className="hidden md:flex items-center gap-5 text-xs text-white/50 font-bold tracking-widest uppercase">
            {[["about", "企劃"], ["issue1", "代表隊"], ["issue2", "包機"], ["issue3", "職籃"], ["faq", "FAQ"]].map(([id, label]) => (
              <button key={id} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })} className="hover:text-accent transition-colors cursor-pointer">{label}</button>
            ))}
          </div>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={scrollToSurvey}
            className="px-4 py-1.5 rounded-full bg-accent text-white text-xs font-black tracking-widest uppercase hover:bg-accent/80 transition-colors shadow-[0_0_14px_rgba(234,88,12,0.4)]">
            立即參與
          </motion.button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <AnimatePresence>
        {!submitted ? (
          <section ref={heroRef} id="hero" className="relative min-h-screen flex flex-col items-center justify-center pt-14 overflow-hidden">
            {/* Parallax BG */}
            <motion.div className="absolute inset-0 z-0" style={{ y: heroY }}>
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover object-top opacity-15" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, oklch(0.09 0.03 250) 0%, transparent 35%, transparent 65%, oklch(0.09 0.03 250) 100%)" }} />
            </motion.div>

            {/* Cyan ambient glow - shows immediately */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.0 }}
              className="absolute inset-0 pointer-events-none z-1"
              style={{ background: "radial-gradient(ellipse 60% 58% at 50% 50%, oklch(0.55 0.16 220 / 0.12) 0%, transparent 68%)" }}
            />

            {/* Typhoon eye - VISIBLE entrance animation */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.62, rotate: -25 }}
                animate={{ opacity: 0.38, scale: 1, rotate: 0 }}
                transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                className="text-cyan-400"
                style={{ marginTop: "-5%" }}
              >
                <TyphoonEye size={620} />
              </motion.div>
            </div>

            {/* Speed lines */}
            <SpeedLines className="absolute top-1/3 left-0 w-72 h-16 text-accent pointer-events-none z-2" />
            <SpeedLines className="absolute bottom-1/3 right-0 w-72 h-16 text-accent pointer-events-none z-2" style={{ transform: "scaleX(-1)" }} />

            {/* Taiwan island - slide in from left */}
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 0.5, x: 0 }}
              transition={{ delay: 0.5, duration: 0.7, ease: "easeOut" }}
              className="absolute top-24 left-8 md:left-20 pointer-events-none z-2 text-cyan-400"
            >
              <TaiwanIsland size={60} />
            </motion.div>

            {/* Rotating basketball - pop in and spin */}
            <motion.div
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{ opacity: 0.5, scale: 1, rotate: 360 }}
              transition={{
                opacity: { duration: 0.5, delay: 0.35 },
                scale: { duration: 0.55, delay: 0.35, type: "spring", stiffness: 280, damping: 18 },
                rotate: { duration: 20, repeat: Infinity, ease: "linear", delay: 0 }
              }}
              className="absolute top-24 right-12 md:right-28 pointer-events-none z-2 text-accent"
            ><BasketballSVG size={90} /></motion.div>

            {/* Floating basketball particles */}
            {(([
              { size: 22, top: "18%", left: "8%", right: undefined, delay: 0.4, dur: 3.2 },
              { size: 16, top: "66%", left: "5%", right: undefined, delay: 1.0, dur: 4.1 },
              { size: 28, top: "36%", left: undefined, right: "6%", delay: 0.7, dur: 3.6 },
              { size: 14, top: "73%", left: undefined, right: "13%", delay: 1.4, dur: 2.9 },
              { size: 20, top: "53%", left: "14%", right: undefined, delay: 0.85, dur: 3.4 },
            ]) as Array<{ size: number; top: string; left?: string; right?: string; delay: number; dur: number }>).map((b, i) => (
              <motion.div
                key={i}
                className="absolute pointer-events-none z-3 text-accent"
                style={{ top: b.top, ...(b.left ? { left: b.left } : { right: b.right }) }}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 0.52, y: [0, -(b.size * 0.9), 0] }}
                transition={{
                  opacity: { delay: b.delay, duration: 0.5 },
                  y: { delay: b.delay + 0.25, duration: b.dur, repeat: Infinity, ease: "easeInOut" }
                }}
              >
                <BasketballSVG size={b.size} />
              </motion.div>
            ))}

            {/* Hero content */}
            <motion.div style={{ opacity: heroOpacity }} className="relative z-10 text-center px-4 max-w-4xl mx-auto">

              {/* Badge */}
              <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, ...spring }}>
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="inline-block text-accent text-xs font-bold tracking-[0.35em] uppercase px-5 py-1.5 border border-accent/40 rounded-full" style={{ fontFamily: "var(--font-mono)" }}>中華籃協改革前測計畫</span>
                </div>
              </motion.div>

              {/* Title - char-by-char spring reveal */}
              <h1
                style={{ fontFamily: "Bebas Neue, var(--font-sans)", letterSpacing: "0.05em", lineHeight: 1 }}
                className="text-7xl md:text-[9.5rem] font-black text-white mb-1 flex justify-center flex-wrap"
              >
                {"TAI 風起".split("").map((char, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 64, scale: 0.65 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: 0.22 + i * 0.07, type: "spring", stiffness: 270, damping: 19 }}
                    style={char === " " ? { display: "inline-block", minWidth: "0.32em" } : {}}
                  >{char}</motion.span>
                ))}
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.82, ...spring }}
                className="text-xl md:text-3xl font-black mb-5 tracking-widest"
                style={{ color: "oklch(0.85 0.05 250)" }}
              >台灣籃球重新集合</motion.p>

              {/* Live counter badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.78 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.0, type: "spring", stiffness: 320, damping: 22 }}
                className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5"
              >
                <motion.span
                  className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0 inline-block"
                  animate={{ opacity: [1, 0.25, 1] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
                />
                <span className="text-cyan-300 text-xs font-bold tracking-widest" style={{ fontFamily: "var(--font-mono)" }}>
                  已有 1,247+ 球迷關注 · 約 3 分鐘完成填答
                </span>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="text-white/50 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-3"
              >代表隊意象・亞運應援・職籃整合・球迷參與調查</motion.p>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.18 }}
                className="text-white/35 text-xs md:text-sm max-w-xl mx-auto leading-relaxed mb-10"
              >邀請你一起決定台灣籃球的下一步。</motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.28, ...spring }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                {/* Pulsing CTA */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={scrollToSurvey}
                  animate={{ boxShadow: ["0 4px 24px rgba(234,88,12,0.42)", "0 4px 46px rgba(234,88,12,0.9)", "0 4px 24px rgba(234,88,12,0.42)"] }}
                  transition={{ boxShadow: { duration: 1.7, repeat: Infinity, ease: "easeInOut" } }}
                  className="px-8 py-4 rounded-full bg-accent text-white font-black text-base tracking-widest uppercase flex items-center gap-2 justify-center"
                >
                  開始前測 <ArrowRight size={18} />
                </motion.button>
                <button
                  onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                  className="px-8 py-4 rounded-full border-2 border-white/20 text-white font-bold text-base tracking-wide hover:border-cyan-400/50 hover:text-cyan-300 transition-all flex items-center gap-2 justify-center cursor-pointer">
                  了解四大議題
                </button>
              </motion.div>
            </motion.div>

            <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2.2, repeat: Infinity }} className="absolute bottom-8 z-10">
              <ChevronDown size={26} className="text-white/25" />
            </motion.div>
          </section>
        ) : null}
      </AnimatePresence>

      {/* ── Success page ── */}
      <AnimatePresence>
        {submitted && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-screen flex flex-col items-center justify-center px-4 pt-14"
          >
            <div className="relative text-center max-w-lg mx-auto">
              <div className="absolute inset-0 -z-10 opacity-20 flex items-center justify-center">
                <TyphoonEye size={500} className="text-cyan-400" />
              </div>
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="text-8xl mb-6">🏀</motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="text-6xl md:text-8xl font-black text-white mb-3"
                style={{ fontFamily: "Bebas Neue, var(--font-sans)", letterSpacing: "0.08em" }}
              >謝謝你！</motion.h2>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-white/60 text-base mb-8 leading-relaxed">
                你的意見將成為中華籃協規劃代表隊品牌、亞運應援、職籃整合與球迷經營的重要參考。<br />
                接下來，籃協將整理前測結果，並公布階段性回饋。
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }} className="p-5 rounded-2xl bg-accent/10 border border-accent/30 text-accent text-sm font-bold mb-8">
                🎁 你已取得抽獎資格！中華颱風隊首批應援商品將以簡訊通知幸運球迷。
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="flex flex-col sm:flex-row gap-3 justify-center">
                <button className="px-6 py-3 rounded-full bg-white/10 text-white font-bold text-sm flex items-center gap-2 justify-center hover:bg-white/15 transition-colors border border-white/15">
                  <Share2 size={16} /> 分享給朋友
                </button>
                <button className="px-6 py-3 rounded-full bg-white/10 text-white font-bold text-sm flex items-center gap-2 justify-center hover:bg-white/15 transition-colors border border-white/15">
                  <ExternalLink size={16} /> 追蹤中華籃協
                </button>
              </motion.div>
              <p className="mt-10 text-white/25 text-xs tracking-widest uppercase" style={{ fontFamily: "var(--font-mono)" }}>Tai 風起，台灣籃球重新集合。</p>
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Landing Content (shown when not in survey) ── */}
      {currentStep === -1 && !submitted && (
        <>
          {/* ── About ── */}
          <section id="about" className="py-24 px-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-8 pointer-events-none text-cyan-400">
              <TaiwanIsland size={200} />
            </div>
            <div className="max-w-4xl mx-auto relative z-10">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <SectionTitle icon={Star} tag="為什麼需要這個前測" title="重大改革正式推動前，先聽見球迷的聲音"
                  sub="過去，很多重要決策常常在球迷已經有感之後才被討論。這一次，新籃協希望換一種方式：先提出方向，先公開測試，先讓真正關心台灣籃球的人表達意見。" />
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-5 mb-12">
                {[
                  { icon: Wind, title: "中華颱風隊", desc: "台灣籃球代表隊是否需要一個可長期累積的隊伍意象？", color: "text-cyan-400", border: "border-cyan-400/20 hover:border-cyan-400/40" },
                  { icon: Plane, title: "亞運應援包機", desc: "球迷是否願意用實際行動支持代表隊出征 2026 名古屋亞運？", color: "text-yellow-400", border: "border-yellow-400/20 hover:border-yellow-400/40" },
                  { icon: BarChart2, title: "職籃整合", desc: "籃協應該用多積極的方式推動台灣職籃整合？", color: "text-accent", border: "border-accent/20 hover:border-accent/40" },
                  { icon: Users, title: "球迷資料建立", desc: "你怎麼看球、買票、進場與應援，會影響台灣籃球未來怎麼規劃。", color: "text-purple-400", border: "border-purple-400/20 hover:border-purple-400/40" },
                ].map((item) => (
                  <motion.div key={item.title} variants={fadeUp}
                    className={`p-6 rounded-2xl border bg-white/3 transition-all cursor-pointer group ${item.border}`}
                    whileHover={{ y: -4, scale: 1.01 }}
                    onClick={scrollToSurvey}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-white/6 flex items-center justify-center mb-4 ${item.color}`}>
                      <item.icon size={18} />
                    </div>
                    <h3 className="font-black text-white text-lg mb-2">{item.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed mb-3">{item.desc}</p>
                    <span className={`text-xs font-bold flex items-center gap-1 ${item.color} opacity-0 group-hover:opacity-100 transition-opacity`}>
                      參與這題 <ChevronRight size={12} />
                    </span>
                  </motion.div>
                ))}
              </motion.div>

              {/* Core quote */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="p-7 rounded-3xl relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, oklch(0.13 0.05 220), oklch(0.09 0.03 250))" }}>
                <div className="absolute top-0 right-0 opacity-15 text-cyan-400 pointer-events-none">
                  <TyphoonEye size={250} />
                </div>
                <blockquote className="text-white/75 text-base leading-relaxed italic relative z-10">
                  「台灣籃球需要的不只是更好的球員，也需要更清楚的代表隊品牌、更好的制度設計、更穩定的資源支持，以及願意一起走出去的球迷。
                  <br /><br />
                  這份前測不會直接決定所有事情，但它會讓籃協更清楚知道球迷真實的想法。」
                </blockquote>
                <div className="mt-5 relative z-10">
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={scrollToSurvey}
                    className="px-6 py-3 rounded-full bg-accent text-white font-black text-sm tracking-widest uppercase flex items-center gap-2 shadow-[0_2px_18px_rgba(234,88,12,0.4)]">
                    開始前測 <ArrowRight size={16} />
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ── Wave divider ── */}
          <div className="relative h-20 overflow-hidden" style={{ color: "oklch(0.07 0.025 250)" }}>
            <OceanWave className="absolute bottom-0 w-full h-full" />
          </div>

          {/* ── Issue 1 teaser ── */}
          <section id="issue1" className="py-24 px-4" style={{ background: "oklch(0.07 0.025 250)" }}>
            <div className="max-w-4xl mx-auto">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <SectionTitle icon={Wind} tag="議題一" title="中華颱風隊：一個可以長期累積的代表隊意象" sub="颱風來自海洋，帶著速度、旋轉、壓迫、防守強度與島嶼能量。「Tai 風」也象徵屬於台灣的球風。" />
              </motion.div>

              {/* 背景說明 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="mb-10 space-y-4 text-white/65 text-sm leading-relaxed">
                <p>國家隊需要的不只是一套球衣，也需要一個能被記住、被傳唱、被年輕球員繼承的共同意象。</p>
                <p>台灣籃球代表隊長期以「中華隊」或「Chinese Taipei」作為主要識別。然而，若希望代表隊能長期累積球迷認同、商品價值與社群聲量，只靠正式隊名並不足夠。</p>
                <p>國際上，日本籃球代表隊長期以 <span className="text-cyan-300 font-bold">AKATSUKI JAPAN</span> 作為代表隊品牌呈現，是值得參考的方向。台灣籃球亦可在既有國際名稱限制下，發展屬於自己的隊伍意象。</p>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-6 items-center">
                <motion.div variants={fadeUp} className="flex justify-center">
                  <div className="relative">
                    <div className="text-cyan-400 opacity-70">
                      <TyphoonEye size={260} />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-white font-black text-2xl" style={{ fontFamily: "Bebas Neue, var(--font-sans)", letterSpacing: "0.1em" }}>CT</p>
                        <p className="text-cyan-400 text-xs font-bold tracking-widest">TYPHOON</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
                <motion.div variants={fadeUp} className="space-y-4">
                  <div className="flex items-start gap-3">
                    <TaiwanIsland size={40} className="text-cyan-400 flex-shrink-0" />
                    <div>
                      <h3 className="text-white font-black text-lg mb-2">為什麼是「颱風」？</h3>
                      <p className="text-white/55 text-sm leading-relaxed">因為台灣是海島，颱風也是所有人都理解的共同經驗。它有速度、有旋轉、有壓迫、有能量，也有從海面生成、席捲全場的強烈畫面。</p>
                      <p className="text-white/45 text-sm leading-relaxed mt-2">如果台灣籃球要有一種代表隊精神，我們希望它能夠像颱風一樣：<span className="text-cyan-300">快速、強悍、集體移動，讓世界感受到來自這座島嶼的球風。</span></p>
                    </div>
                  </div>

                  {/* 代表隊品牌系統概念 */}
                  <div className="p-4 rounded-xl border border-cyan-400/15 bg-cyan-400/4 space-y-2">
                    <p className="text-cyan-300 text-sm font-bold">未來各級代表隊共用同一意象系統</p>
                    <p className="text-white/50 text-xs leading-relaxed">無論是男籃、女籃、三對三，都可以在「中華颱風隊」的應援品牌下，建立統一的視覺與應援文化，逐步形成跨世代的代表隊認同。</p>
                  </div>

                  <div className="p-4 rounded-xl border border-cyan-400/20 bg-cyan-400/6">
                    <p className="text-cyan-300 text-sm font-bold mb-1">Chinese Taipei／CT 仍是國際賽事正式名稱</p>
                    <p className="text-white/50 text-xs leading-relaxed">「中華颱風隊」不是取代正式名稱，而是希望在 Chinese Taipei／CT 的現有框架下，發展一套可長期累積的代表隊應援意象與品牌系統。</p>
                  </div>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} onClick={scrollToSurvey}
                    className="px-6 py-3 rounded-full border-2 border-cyan-400/40 text-cyan-300 font-bold text-sm flex items-center gap-2 hover:bg-cyan-400/10 transition-colors">
                    投票選出你支持的設計 <ChevronRight size={15} />
                  </motion.button>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ── Wave up ── */}
          <div className="relative h-20 overflow-hidden" style={{ color: "oklch(0.09 0.03 250)", transform: "scaleY(-1)" }}>
            <OceanWave className="absolute bottom-0 w-full h-full" />
          </div>

          {/* ── Issue 2 teaser ── */}
          <section id="issue2" className="py-24 px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <SectionTitle icon={Plane} tag="議題二" title="跟著代表隊出發，把應援帶到亞運現場" sub="2026 年 9 月，愛知・名古屋亞運將登場。籃協正在前測「亞運應援包機」方案。" accent />
              </motion.div>

              {/* 背景說明 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="mb-10 space-y-4 text-white/65 text-sm leading-relaxed">
                <p>大家都希望代表台灣出國比賽的選手，能有更好的交通與住宿待遇。但現階段籃協可使用的主要資源仍有限，交通與住宿標準多以經濟艙與基本住宿為主。</p>
                <p>新籃協希望一方面持續爭取政府與企業支持，另一方面也開發讓球迷能直接參與代表隊應援的方式——讓球迷的熱情，也能成為支持選手更好出征條件的力量。</p>
                <p>2026 年名古屋亞運，男籃、女籃與三對三籃球皆具有高度應援價值，籃協希望先透過前測了解球迷意願，再決定是否正式推進。</p>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-4">
                {[
                  { icon: "✈️", title: "去程包機機位", desc: "台灣至名古屋／中部地區" },
                  { icon: "🎫", title: "A 席賽事票券", desc: "預賽階段中華隊比賽" },
                  { icon: "🎁", title: "首發限量應援包", desc: "中華颱風隊限量周邊" },
                  { icon: "🎙️", title: "機上應援體驗", desc: "機上限定球員互動廣播" },
                  { icon: "💛", title: "支持女籃升艙", desc: "盈餘專款支持女籃與三對三選手升艙或移地支援" },
                  { icon: "🏅", title: "與代表隊同行", desc: "籃協將爭取讓球迷與代表隊在同一段旅程出發" },
                ].map((item) => (
                  <motion.div key={item.title} variants={fadeUp}
                    className="p-5 rounded-2xl border border-yellow-400/15 bg-yellow-400/4 hover:border-yellow-400/30 transition-colors group"
                    whileHover={{ y: -3 }}
                  >
                    <span className="text-3xl block mb-3">{item.icon}</span>
                    <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                    <p className="text-white/45 text-xs">{item.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* 前測聲明 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="mt-8 p-5 rounded-2xl border border-white/10 bg-white/3">
                <p className="text-white/55 text-xs font-bold uppercase tracking-widest mb-2" style={{ fontFamily: "var(--font-mono)" }}>📋 前測聲明</p>
                <p className="text-white/50 text-sm leading-relaxed">本方案目前為意願調查與合作洽詢階段，<span className="text-white/75">實際價格、航班、票種、賽事場次、應援包內容與報名方式，均以後續正式公告為準。</span>籃協將依代表隊行程、航空安全、座位配置與管理規範，確認實際搭乘安排。</p>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-8 flex justify-center">
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={scrollToSurvey}
                  className="px-6 py-3 rounded-full border-2 border-yellow-400/40 text-yellow-300 font-bold text-sm flex items-center gap-2 hover:bg-yellow-400/10 transition-colors">
                  填寫亞運應援意願 <ChevronRight size={15} />
                </motion.button>
              </motion.div>
            </div>
          </section>

          {/* ── Issue 3 teaser ── */}
          <section id="issue3" className="py-24 px-4" style={{ background: "oklch(0.07 0.025 250)" }}>
            <div className="max-w-4xl mx-auto">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <SectionTitle icon={BarChart2} tag="議題三" title="台灣職籃，該怎麼整合？" sub="籃協會積極促成整合，也會評估完整規章、聯賽認證、裁判制度、外籍球員登錄、台灣大賽等制度工具。但要走到哪一步，球迷的意見非常重要。" />
              </motion.div>

              {/* 背景說明 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="mb-10 space-y-4 text-white/65 text-sm leading-relaxed">
                <p>台灣籃球長期面臨資源分散、聯盟並立、制度不一與球迷市場分裂等問題。球迷期待看到最好的本土球員、外籍球員、教練與球隊在同一個最高層級競爭；但職籃整合涉及球團利益、賽務制度、商業安排與長期發展，需要被清楚討論。</p>
                <p>此外，<span className="text-white/80">SBL 在制度上仍具重要歷史與基礎角色</span>，但其競技強度與商業能量已難以被普遍視為現階段台灣籃球的最高層級。新籃協若希望重建整體籃球秩序，必須積極促成頂級聯賽制度整合，並重新界定 SBL、職籃、UBA、HBL、基層籃球與代表隊養成之間的關係。</p>
                <p>新籃協的立場是：台灣籃球需要更清楚的頂級聯賽制度，讓 SBL、職籃、UBA、HBL 與基層養成形成更健康的階層關係。但要走到哪一步，球迷的意見非常重要。</p>
              </motion.div>

              {/* A/B/C 立場卡 - 帶完整描述 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-4 mb-8">
                {[
                  {
                    label: "A", title: "積極要求並促成合併",
                    desc: "籃協應使用最積極的制度工具，促成單一頂級職籃；SBL 轉型為下層發展聯盟，類似 NBA 與 G League 的上下層關係。",
                    color: "border-red-400/30 bg-red-400/5 hover:border-red-400/50",
                    tag: "text-red-400",
                  },
                  {
                    label: "B", title: "積極協調，軟性促成",
                    desc: "籃協應協助兩聯盟合作與合併，但不急著使用強硬手段；台灣大賽可視兩聯盟意願逐步推進。",
                    color: "border-orange-400/30 bg-orange-400/5 hover:border-orange-400/50",
                    tag: "text-orange-400",
                  },
                  {
                    label: "C", title: "先促成台灣大賽即可",
                    desc: "籃協應優先主辦或促成跨聯盟台灣大賽，至於兩聯盟是否正式合併，可留給市場與球團決定。",
                    color: "border-blue-400/30 bg-blue-400/5 hover:border-blue-400/50",
                    tag: "text-blue-400",
                  },
                ].map((item) => (
                  <motion.div key={item.label} variants={fadeUp} whileHover={{ y: -4 }} onClick={scrollToSurvey}
                    className={`p-6 rounded-2xl border cursor-pointer transition-all ${item.color}`}>
                    <div className={`text-3xl font-black mb-3 ${item.tag}`} style={{ fontFamily: "Bebas Neue, var(--font-sans)" }}>{item.label}</div>
                    <p className="text-white font-bold text-sm mb-3">{item.title}</p>
                    <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
                    <p className="text-white/30 text-xs mt-4 flex items-center gap-1">點擊表達立場 <ChevronRight size={11} /></p>
                  </motion.div>
                ))}
              </motion.div>

              {/* 制度工具說明 */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                className="p-5 rounded-2xl border border-accent/15 bg-accent/4">
                <p className="text-accent text-sm font-bold mb-3">籃協可評估的制度工具包括：</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {["單一頂級聯賽制度設計", "聯賽認證與分級制度", "外籍球員登錄規範", "裁判與技術代表制度", "台灣大賽跨聯盟冠軍賽", "SBL 轉型為發展聯盟"].map((tool) => (
                    <div key={tool} className="flex items-center gap-2 text-white/50 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent/60 flex-shrink-0" />
                      {tool}
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-8 flex justify-center">
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={scrollToSurvey}
                  className="px-6 py-3 rounded-full border-2 border-accent/40 text-accent font-bold text-sm flex items-center gap-2 hover:bg-accent/10 transition-colors">
                  表達你的職籃整合立場 <ChevronRight size={15} />
                </motion.button>
              </motion.div>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section id="faq" className="py-24 px-4">
            <div className="max-w-3xl mx-auto">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
                <SectionTitle icon={HelpCircle} tag="FAQ" title="常見問題" />
              </motion.div>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-3">
                {faqs.map((faq, i) => (
                  <motion.div key={i} variants={fadeUp}>
                    <Accordion q={faq.q} a={faq.a} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── Final CTA ── */}
          <section className="py-20 px-4" style={{ background: "oklch(0.07 0.025 250)" }}>
            <div className="max-w-2xl mx-auto text-center relative overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center opacity-10 text-cyan-400 pointer-events-none">
                <TyphoonEye size={400} />
              </div>
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: "Bebas Neue, var(--font-sans)", letterSpacing: "0.06em" }}>
                  Tai 風起，請你一起決定下一步
                </h2>
                <p className="text-white/55 text-sm leading-relaxed mb-8">台灣籃球的下一步，不只在球場上，也在每一位願意參與的球迷手上。謝謝你一起讓台灣籃球重新集合。</p>
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} onClick={scrollToSurvey}
                  className="px-10 py-4 rounded-full bg-accent text-white font-black text-lg tracking-widest uppercase flex items-center gap-3 justify-center mx-auto shadow-[0_4px_32px_rgba(234,88,12,0.55)]">
                  開始前測 <ArrowRight size={20} />
                </motion.button>
              </motion.div>
            </div>
          </section>
        </>
      )}

      {/* ── Survey Steps ── */}
      {currentStep >= 0 && !submitted && (
        <div ref={surveyRef}>
          <StepProgress current={currentStep} />
          <div className="py-16 px-4">
            <div className="max-w-3xl mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                >
                  {currentStep === 0 && <StepBrand data={surveyData} setData={setSurveyData} />}
                  {currentStep === 1 && <StepCharter data={surveyData} setData={setSurveyData} />}
                  {currentStep === 2 && <StepLeague data={surveyData} setData={setSurveyData} />}
                  {currentStep === 3 && <StepFanData data={surveyData} setData={setSurveyData} />}
                  {currentStep === 4 && <StepVerify data={surveyData} setData={setSurveyData} onSubmit={handleSubmit} />}
                </motion.div>
              </AnimatePresence>
              {currentStep < 4 && (
                <NavButtons onPrev={handlePrev} onNext={handleNext} step={currentStep} total={5} canNext />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      {currentStep === -1 && !submitted && (
        <footer className="py-10 px-4 border-t border-white/8">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-center md:text-left">
            <div className="flex items-center gap-2">
              <BasketballSVG size={18} className="text-accent" />
              <span className="text-white/50 text-xs font-bold tracking-wide">TAI 風起 · 中華籃協改革前測計畫</span>
            </div>
            <div className="flex items-center gap-2 text-white/30 text-xs">
              <Trophy size={12} />
              <span>本前測非正式公告，也非最終決策。一切以後續官方公告為準。</span>
            </div>
            <p className="text-white/20 text-xs">© 2026 中華籃球協會</p>
          </div>
        </footer>
      )}
    </div>
  );
}
